import { useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type RowData,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

// Extend TanStack Table meta
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
  }
}

import { Check, Circle, List, Loader, XCircle } from 'lucide-react';

import {
  type BatchJob,
  type Job,
  JobStatus,
  loraApi,
  type PaginatedResponse,
} from '@/api/connectivity/lora';
import { Badge } from '@/components/ui/badge';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

// Job category labels
const JOB_CATEGORY_LABELS: Record<string, string> = {
  'activate-device': 'Ativar dispositivo',
  'deactivate-device': 'Desativar dispositivo',
  'create-device': 'Criar dispositivo',
};

// Status badge component
function StatusBadge({ status }: { status: JobStatus }) {
  switch (status) {
    case JobStatus.JS_PROCESSING:
      return (
        <Badge variant="outline" className="gap-1">
          <Loader size={12} className="animate-spin text-yellow-500" />
          Processando
        </Badge>
      );
    case JobStatus.JS_SUCCESS:
      return (
        <Badge variant="outline" className="gap-1">
          <Check size={12} className="text-green-500" />
          Finalizado
        </Badge>
      );
    case JobStatus.JS_FAILED:
      return (
        <Badge variant="outline" className="gap-1">
          <XCircle size={12} className="text-red-500" />
          Falhou
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="gap-1">
          <Circle size={12} className="text-muted-foreground" />
          Pendente
        </Badge>
      );
  }
}

// Single Job Logs Table
interface DeviceActivationLogTableProps {
  data: PaginatedResponse<Job>;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export function DeviceActivationLogTable({
  data,
  currentPage,
  onPageChange,
}: DeviceActivationLogTableProps) {
  const { docs, pages = 1, total = 0, limit = 20 } = data;

  const columns: ColumnDef<Job>[] = useMemo(
    () => [
      {
        header: 'Criado em',
        accessorKey: 'created_at',
        cell: ({ row }) => {
          try {
            return format(new Date(row.original.created_at), 'dd/MM/yyyy HH:mm:ss');
          } catch {
            return '--';
          }
        },
        meta: { className: 'w-44' },
      },
      {
        header: 'Autor',
        accessorKey: 'author.name',
        cell: ({ row }) => {
          const authorName = row.original.payload.author?.name;
          return (
            <div className="w-52 overflow-hidden whitespace-nowrap text-ellipsis">
              <span>{authorName ?? '--'}</span>
            </div>
          );
        },
        meta: { className: 'w-52' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
        meta: { className: 'w-36' },
      },
      {
        header: 'Serial',
        accessorKey: 'payload.serial',
        cell: ({ row }) => row.original.payload.serial,
      },
      {
        header: 'Requisição',
        accessorKey: 'payload',
        cell: ({ row }) => JOB_CATEGORY_LABELS[row.original.payload.job_category] ?? '--',
      },
      {
        header: 'Finalizou em',
        accessorKey: 'completed_at',
        cell: ({ row }) => {
          const completedAt = row.original.completed_at;
          if (completedAt) {
            try {
              return format(new Date(completedAt), 'dd/MM/yyyy HH:mm:ss');
            } catch {
              return '--';
            }
          }
          return '--';
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: docs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border w-full bg-background shadow-sm">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(header.column.columnDef.meta?.className)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Nenhum dado encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="overflow-hidden rounded-md border">
          <Pagination
            currentPage={currentPage}
            totalPages={pages}
            totalItems={total}
            limit={limit}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
}

// Batch Job Logs Table with Modal
interface BatchDeviceActivationLogTableProps {
  data: PaginatedResponse<BatchJob>;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export function BatchDeviceActivationLogTable({
  data,
  currentPage,
  onPageChange,
}: BatchDeviceActivationLogTableProps) {
  const { docs, pages = 1, total = 0, limit = 20 } = data;
  const [selectedBatch, setSelectedBatch] = useState<BatchJob['batch_metadata'] | null>(null);

  // Query for related jobs when a batch is selected
  const { data: relatedJobs, isLoading: isLoadingRelatedJobs } = useQuery({
    queryKey: ['batch-related-jobs', selectedBatch?.uuid],
    queryFn: () => loraApi.findJobsByBatchUuid(selectedBatch?.uuid!),
    enabled: !!selectedBatch?.uuid,
  });

  const handleViewRelatedJobs = useCallback((batchMetadata: BatchJob['batch_metadata']) => {
    setSelectedBatch(batchMetadata);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedBatch(null);
  }, []);

  const columns: ColumnDef<BatchJob>[] = useMemo(
    () => [
      {
        header: 'Criado em',
        accessorKey: 'created_at',
        cell: ({ row }) => {
          try {
            return format(new Date(row.original.created_at), 'dd/MM/yyyy HH:mm:ss');
          } catch {
            return '--';
          }
        },
        meta: { className: 'w-44' },
      },
      {
        header: 'Autor',
        accessorKey: 'author.name',
        cell: ({ row }) => {
          const authorName = row.original.author?.name;
          return (
            <div className="w-52 overflow-hidden whitespace-nowrap text-ellipsis">
              <span>{authorName ?? '--'}</span>
            </div>
          );
        },
        meta: { className: 'w-52' },
      },
      {
        header: 'Requisição',
        accessorKey: 'payload',
        cell: ({ row }) => JOB_CATEGORY_LABELS[row.original.job_category] ?? '--',
      },
      {
        id: 'action',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="!h-8"
              onClick={() => handleViewRelatedJobs(row.original.batch_metadata)}
            >
              <List />
              Ver logs relacionados
            </Button>
          </div>
        ),
        meta: { className: 'w-32' },
      },
    ],
    [handleViewRelatedJobs],
  );

  const table = useReactTable({
    data: docs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Related jobs columns for the modal
  const relatedJobsColumns: ColumnDef<Job>[] = useMemo(
    () => [
      {
        header: 'Criado em',
        accessorKey: 'created_at',
        cell: ({ row }) => {
          try {
            return format(new Date(row.original.created_at), 'dd/MM/yyyy HH:mm:ss');
          } catch {
            return '--';
          }
        },
        meta: { className: 'w-44' },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
        meta: { className: 'w-36' },
      },
      {
        header: 'Serial',
        accessorKey: 'payload.serial',
        cell: ({ row }) => row.original.payload.serial,
      },
      {
        header: 'Requisição',
        accessorKey: 'payload',
        cell: ({ row }) => JOB_CATEGORY_LABELS[row.original.payload.job_category] ?? '--',
      },
    ],
    [],
  );

  const relatedJobsTable = useReactTable({
    data: relatedJobs?.docs ?? [],
    columns: relatedJobsColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-md border w-full bg-background shadow-sm">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(header.column.columnDef.meta?.className)}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>

            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    Nenhum dado encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {pages > 1 && (
          <div className="overflow-hidden rounded-md border">
            <Pagination
              currentPage={currentPage}
              totalPages={pages}
              totalItems={total}
              limit={limit}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>

      {/* Related Jobs Modal */}
      <Dialog open={!!selectedBatch} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-[60vw] max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              Logs relacionados - {selectedBatch?.file_name || selectedBatch?.uuid}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            {isLoadingRelatedJobs ? (
              <div className="flex justify-center py-8">
                <Loader className="animate-spin" />
              </div>
            ) : (
              <div className="rounded-md border w-full bg-background shadow-sm">
                <Table>
                  <TableHeader>
                    {relatedJobsTable.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(header.column.columnDef.meta?.className)}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>

                  <TableBody>
                    {relatedJobsTable.getRowModel().rows.length ? (
                      relatedJobsTable.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={relatedJobsColumns.length} className="h-24 text-center">
                          Nenhum job encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
